import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Code, ShoppingCart, Smartphone, TrendingUp, Search, MessageSquare, Palette, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  {
    icon: ShoppingCart,
    title: 'E-commerce Development',
    description: 'Enterprise commerce platforms for B2B and B2C brands',
    features: [
      'HCL Commerce implementations',
      'MedusaJS storefronts',
      'Headless commerce migrations',
      'B2B pricing & catalog logic',
      'Payment gateway integration',
    ],
    technologies: ['HCL Commerce', 'MedusaJS', 'React', 'Next.js'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: TrendingUp,
    title: 'Custom Software Development',
    description: 'Custom integrations connecting commerce platforms to your business systems',
    features: [
      'ERP & PIM integrations',
      'API development',
      'Fulfillment & inventory sync',
      'Platform migrations',
      'Performance optimization',
    ],
    technologies: ['Node.js', 'React', 'Next.js'],
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: Code,
    title: 'Website Development',
    description: 'Professional, responsive websites that drive business growth',
    features: [
      'Custom website design',
      'Responsive & mobile-friendly',
      'Fast loading & SEO optimized',
      'Ongoing maintenance & support',
    ],
    technologies: ['React', 'Next.js', 'Node.js'],
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Mobile experiences delivered through trusted development partners',
    features: [
      'iOS & Android apps',
      'Cross-platform development',
      'App design & UX',
      'Maintenance & updates',
    ],
    technologies: [], // consider removing this card or being specific once you actually deliver mobile work
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: Search,
    title: 'SEO Services',
    description: 'Technical SEO for commerce platforms and migrations',
    features: [
      'Technical SEO audits',
      'Headless/SPA crawlability fixes',
      'Migration redirect mapping',
      'Performance tracking',
    ],
    technologies: ['Google Analytics', 'Search Console'],
    image: 'https://images.unsplash.com/photo-1571171637578-41bc2dd41cd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: MessageSquare,
    title: 'Digital Marketing',
    description: 'Marketing support delivered through trusted partners',
    features: [
      'Campaign strategy guidance',
      'Conversion optimization',
    ],
    technologies: [], // same note as Mobile App — consider removing until this is a real service line
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: Palette,
    title: 'UI/UX Design',
    description: 'Interfaces designed for conversion and usability',
    features: [
      'Wireframing & prototyping',
      'Visual design',
      'Usability testing',
    ],
    technologies: ['Figma'],
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
  {
    icon: Users,
    title: 'Business Consulting',
    description: 'Strategic guidance for e-commerce platform decisions',
    features: [
      'Technology & platform selection',
      'Digital transformation strategy',
      'Migration planning',
    ],
    technologies: ['Agile', 'Cloud'],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  },
];

export default function ServicesPage() {
  return (
    <>
      <Helmet>
        <title>Our Services - FuturePath Technologies</title>
        <meta name="description" content="Comprehensive software development services: web development, mobile apps, e-commerce, SEO, digital marketing, and consulting." />
      </Helmet>

      <section className="relative min-h-[50vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="services-hero-title">
              Our Services
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive digital solutions to transform your business
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="services-list-section">
        <div className="container-custom">
          <div className="space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="space-y-8">
                    {/* Icon */}
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      <service.icon size={40} className="text-white" />
                    </div>

                    {/* Title & Description */}
                    <div>
                      <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
                        {service.title}
                      </h2>
                      <p className="text-muted-foreground text-lg">
                        {service.description}
                      </p>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                            <CheckCircle size={16} className="text-primary" />
                          </div>
                          <span className="text-white text-base">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-3 pt-4">
                      {service.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-4 py-2 rounded-lg bg-primary/10 text-primary text-sm font-mono border border-primary/20"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-3xl blur-3xl" />
                    <div className="relative glass-card rounded-3xl overflow-hidden p-2">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-auto rounded-2xl object-cover"
                      />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface/30" data-testid="cta-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-6 text-white">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Let's discuss your project and find the perfect solution for your business.
            </p>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-primary hover:bg-primary/90 neon-glow"
              data-testid="cta-btn"
            >
              <Link to="/consultation">
                Schedule Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
}
